<template>
  <div class="instant-result__section">
    <h3 class="instant-result__header">
      {{ translations.title }}

      <span v-if="data.totalCount > 0" class="instant-result__header-count">
        ({{ data.totalCount > data.pageSize ? data.pageSize : data.totalCount }}/{{ totalCountDisplay }})
      </span>

      <a v-if="data.totalCount > 0" :href="searchResultUrl" class="instant-result__show-all-link">{{ tStatic('showAll') }}</a>
    </h3>

    <div v-if="data.totalCount === 0" class="instant-result__no-result">
      {{ tStatic('noResults') }}
    </div>

    <template v-else>
      <ul v-element-loader="{ endpoint }" class="instant-result__list">
        <!-- The key carries the position: a row with no code, or two rows sharing one, made the
             patcher pair the wrong vnodes, and the image kept its old prop - a new article number
             beside the previous product's picture (OC-3079). -->
        <li v-for="(item, index) in data.data" :key="`item-${item.code}-${index}`" :class="['instant-result__item', { 'instant-result__item--internal': isInternal(item) }]">
          <p v-if="isInternal(item) " class="caption instant-result__internal-info">
            <oie-base-icon icon="warning"></oie-base-icon>
            <span v-html="tStatic('internalItemContent')"></span>
          </p>
          <a :href="item.url" class="instant-result__link" :class="{ 'instant-result__is-active': activeSearchItem === item }">
            <oie-base-image
              :images="(item.imageUrl && useImage) ? getAssetPath(`${item.imageUrl}?preset=ProductSmall&watermark=OIEWatermarkSmall`) : getAssetPath('/globalassets/missing404/404.png?preset=ProductThumb')"
              :alt-text="stripHtml(item.name)"
              image-width="3.75rem"
              image-height="3.75rem"
              class="instans-result__product-image"></oie-base-image>

            <div class="instant-result__name-container">
              <div v-html="renderHighlight(item.nameHighlight, item.name)"></div>
              <span v-if="item.itemId" class="instant-result__article-number">{{ tStatic('articleNumberLabel') }} <span v-html="renderHighlight(item.itemIdHighlight, item.itemId)"></span></span>
              <span v-if="item.referenceNumberHighlight" class="instant-result__reference-number">{{ tStatic('referenceNumberLabel') }} <span v-html="renderHighlight(item.referenceNumberHighlight)"></span></span>
              <span v-if="item.descriptionHighlight" class="instant-result__description">{{ tStatic('descriptionLabel') }}   <span v-html="renderHighlight(item.descriptionHighlight)"></span></span>
              <span v-if="item.specificationMatches && item.specificationMatches.length > 0" class="instant-result__specification">
                <span v-for="(specMatch, specIndex) in item.specificationMatches" :key="`spec-${specIndex}`">
                  <span class="instant-result__specification-key">{{ specMatch.key }}</span> <span v-html="renderHighlight(specMatch.value)"></span>
                </span>
              </span>
              <span class="instant-result__breadcrumbs">{{ item.parentCategories }}</span>
            </div>
          </a>
        </li>
      </ul>
    </template>
  </div>
</template>

<script>
import { createSearchResultQueryParams, renderHighlight } from '@/core/search-service'
import { productStatuses } from '@/variables/product-statuses'

export default {
  props: {
    translations: {
      type: Object,
      required: true
    },
    endpoint: {
      type: String,
      required: true
    },
    data: {
      type: Object,
      required: true
    },
    useImage: {
      type: Boolean,
      default: false
    },
    entities: {
      type: Object,
      required: true
    }
  },
  computed: {
    searchResultUrl() {
      const params = createSearchResultQueryParams(this.$store.getters['ui/lastSearchQuery'], this.entities, this.endpoint)
      return `${this.clientModel.searchResultPageUrl}?${params}`
    },
    activeSearchItem() {
      return this.$store.getters['ui/activeSearchItem']
    },
    totalCountDisplay() {
      return this.data.totalCount > 200 ? '200+' : this.data.totalCount
    }
  },
  methods: {
    renderHighlight,
    stripHtml: function(html) {
      return html.replace(/<\/?[^>]+(>|$)/g, '')
    },
    isInternal(item) {
      return (item.publicationStatusCode === productStatuses.internal) || false
    }
  }
}
</script>

<style lang="scss">
.instant-result__section {
  flex-grow: 1;
}

.instant-result__header {
  @include h3-semi-bold;
  display: flex;
  align-items: center;
  margin: 0;
  padding-bottom: measure(quarter);
  border-bottom: 1px solid color(border-gray);
}

.instant-result__header-count {
  @include body-smaller-semi-bold;
}

.instant-result__list {
  list-style-type: none;
  padding: measure(quarter) 0;
  margin: 0;
  min-height: 3rem;
}

.instant-result__list > li:not(:last-child) {
  margin-bottom: measure(eighth);
}

.instant-result__item--internal {
  border: 4px solid color(superuser-primary);
}

.instant-result__internal-info {
  display: flex;
  gap: measure(eighth);
  align-items: center;
  justify-content: center;
  padding: measure(eighth);
  background-color: color(superuser-secondary);
  border-bottom: 4px solid color(superuser-primary);
}

.instant-result__no-result {
  padding: measure(quarter) 0;
}

.instant-result__is-active {
  background-color: lightblue;
}

.instant-result__link {
  @include body-smaller;
  display: flex;
  align-items: center;
  padding: measure(eighth);
  color: color(text-blue);
  text-decoration: none;

  &:hover {
    background-color: color(search-hover);
    cursor: pointer;
  }

  @include media(medium) {
    @include body;
  }
}

.instans-result__product-image {
  flex-shrink: 0;
  width: 3.75rem;
  height: 3.75rem;
  margin-right: measure(half);
  object-fit: contain;
}

.instant-result__type {
  @include body-smaller-bold;
  color: color(text-gray);
  text-transform: uppercase;
  flex-shrink: 0;
  margin-left: auto;
  padding-left: measure(quarter);
}

.instant-result__show-all-link {
  @include body-semi-bold;
  color: color(text-blue);
  text-decoration: none;
  margin-left: measure(half);

  &:hover {
    cursor: pointer;
    text-decoration: underline;
  }
}

.instant-result__name-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.instant-result__name-container em {
  background-color: #44B1FA;
  font-style: normal;
  color: white;
  padding: 0rem .1rem 0rem .05rem;
}

.instant-result__name-container em.instant-result__complete_match {
  background-color: #006db6;
  padding: .15rem .4rem .15rem .4rem;
  border-radius: 2px;
}

.instant-result__article-number, .instant-result__reference-number, .instant-result__description, .instant-result__specification {
  @include caption;
  color: rgb(34, 34, 34);
}

.instant-result__breadcrumbs {
font-size: .9rem;
color: rgb(34, 34, 34);
}

.instant-result__disabled_link {
  color: grey;
}
</style>
